import { useCallback } from 'react';
import type { RecordSourceSelectorProxy, RecordProxy } from 'relay-runtime';

import { REORDER_COLUMNS } from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type {
	ColumnConfigChangeType,
	OptimisticFieldSets,
} from '@atlassian/jira-native-issue-table/src/common/types.tsx';

/**
 * The useColumnsUpdater only support columns reordering (including column removal)
 */
export const useColumnsUpdater = (connectionId: string) => {
	return useCallback(
		(
			store: RecordSourceSelectorProxy,
			changeType: ColumnConfigChangeType,
			optimisticFieldSets: OptimisticFieldSets | undefined,
		) => {
			if (changeType !== REORDER_COLUMNS || !optimisticFieldSets) {
				return;
			}

			const fieldSetsConnection = store.get(connectionId);
			if (!fieldSetsConnection) {
				return;
			}

			let edges = fieldSetsConnection.getLinkedRecords('edges');
			if (!edges) {
				return;
			}

			const findIndexOfOptimisticEdge = (edge: RecordProxy<{}>) => {
				if (!edge) {
					return -1;
				}
				return optimisticFieldSets.edges.findIndex(
					(optimisticEdge) =>
						optimisticEdge.node.fieldSetId ===
						edge?.getLinkedRecord('node')?.getValue('fieldSetId'),
				);
			};

			const totalCount = fieldSetsConnection.getValue('totalCount');
			if (totalCount !== optimisticFieldSets.totalCount) {
				fieldSetsConnection.setValue(optimisticFieldSets.totalCount, 'totalCount');
			}

			// if columns were removed, remove the edges
			if (optimisticFieldSets.edges.length < edges.length) {
				edges = edges?.filter((edge) => findIndexOfOptimisticEdge(edge) !== -1);
			}

			// then sort edges
			edges.sort((a, b) => findIndexOfOptimisticEdge(a) - findIndexOfOptimisticEdge(b));

			fieldSetsConnection.setLinkedRecords(edges, 'edges');
		},
		[connectionId],
	);
};
